import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../contexts/WebSocketContext'
import { useIoT } from '../contexts/IoTContext'
import { useNotifications } from '../contexts/NotificationContext'
import DeviceCard from '../components/DeviceCard'
import {
  Squares2X2Icon,
  ListBulletIcon,
  PlusIcon,
  CpuChipIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  HomeIcon
} from '@heroicons/react/24/outline'


function Dashboard() {
  const { user } = useAuth()
  const { isConnected, sendMessage, messages } = useWebSocket()
  const { devices, stats, toggleDevice, updateDeviceValue, getRooms } = useIoT()
  const { showSuccess } = useNotifications()
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [selectedRoom, setSelectedRoom] = useState('all')

  const rooms = getRooms()
  
  const filteredDevices = selectedRoom === 'all' 
    ? devices 
    : devices.filter(device => device.room === selectedRoom)

  const handleSendMessage = () => {
    sendMessage({
      type: 'test',
      message: 'Mensaje de prueba desde el dashboard',
      timestamp: new Date().toISOString()
    })
    showSuccess('Mensaje enviado via WebSocket')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard IoT</h1>
          <p className="mt-2 text-gray-600">
            Control y monitoreo de dispositivos inteligentes
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>
          <button className="btn-primary flex items-center space-x-2">
            <PlusIcon className="h-4 w-4" />
            <span>Agregar Dispositivo</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CpuChipIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Dispositivos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.totalDevices}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <WifiIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    En Línea
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.onlineDevices}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <WifiIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Desconectados
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.offlineDevices}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Batería Baja
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.lowBatteryDevices}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Room Filter */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Dispositivos por Habitación
            </h3>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Filtrar:</label>
              <select
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="all">Todas las habitaciones</option>
                {rooms.map(room => (
                  <option key={room} value={room}>{room}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Devices Grid/List */}
      <div className={viewMode === 'grid' 
        ? "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" 
        : "space-y-4"
      }>
        {filteredDevices.map((device) => (
          <DeviceCard
            key={device.id}
            device={device}
            onToggle={toggleDevice}
            onUpdate={updateDeviceValue}
          />
        ))}
      </div>

      {filteredDevices.length === 0 && (
        <div className="text-center py-12">
          <HomeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay dispositivos</h3>
          <p className="mt-1 text-sm text-gray-500">
            {selectedRoom === 'all' 
              ? 'Comienza agregando tu primer dispositivo IoT.'
              : `No hay dispositivos en ${selectedRoom}.`
            }
          </p>
        </div>
      )}

      {/* WebSocket Status */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Estado de Conexión
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                WebSocket: {isConnected ? 'Conectado' : 'Desconectado'} | 
                Mensajes: {messages.length}
              </p>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!isConnected}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Probar Conexión
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard